import SearchIcon from '@mui/icons-material/Search';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DATA_ATTRS } from '../ColumnConfiguratorDrawer.styles';
import type { GroupedColumns } from '../ColumnConfiguratorDrawer.types';
import { ColumnPickerItem } from './ColumnPickerItem';

interface ColumnListProps {
  groupedColumns: GroupedColumns[];
  visibleKeys: Set<string>;
  isAtMax: boolean;
  onToggle: (key: string) => void;
  hasNoResults: boolean;
}

export function ColumnList({
  groupedColumns,
  visibleKeys,
  isAtMax,
  onToggle,
  hasNoResults,
}: ColumnListProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, key: string) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const container = (e.currentTarget as HTMLElement).closest(`[${DATA_ATTRS.columnList}]`);
    if (!container) return;
    const allButtons = Array.from(
      container.querySelectorAll<HTMLButtonElement>(`[${DATA_ATTRS.columnKey}]`),
    );
    const idx = allButtons.findIndex(btn => btn.dataset.columnKey === key);
    allButtons[e.key === 'ArrowDown' ? idx + 1 : idx - 1]?.focus();
  }, []);

  return (
    <Box
      {...{ [DATA_ATTRS.columnList]: true }}
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 2,
        py: 1.5,
        bgcolor: 'var(--gray-50)',
        '&::-webkit-scrollbar': { width: 'var(--scrollbar-width)' },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'var(--gray-300)', borderRadius: 3 },
        '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'var(--gray-400)' },
      }}
    >
      {groupedColumns.map(({ config: group, columns }) => (
        <Box key={group.key} sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--gray-500)' }}>
              <group.icon fontSize="small" />
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--gray-500)',
                  fontWeight: theme.typography.fontWeightBold,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontSize: 'var(--font-group)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t(group.labelKey)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'var(--gray-200)', ml: 0.5 }} />
          </Box>

          <Stack spacing={0.75}>
            {columns.map(col => {
              const key = String(col.key);
              return (
                <ColumnPickerItem
                  key={key}
                  columnKey={key}
                  label={t(col.labelKey)}
                  isVisible={visibleKeys.has(key)}
                  isDisabled={!visibleKeys.has(key) && isAtMax}
                  primary={primary}
                  onToggle={onToggle}
                  onKeyDown={handleKeyDown}
                />
              );
            })}
          </Stack>
        </Box>
      ))}

      {hasNoResults && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <SearchIcon sx={{ fontSize: 'var(--icon-empty)', color: 'var(--gray-300)', mb: 1 }} />
          <Typography sx={{ color: 'var(--gray-400)', fontSize: 'var(--font-counter-value)' }}>
            {t('columnConfigurator.noResults')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
